// apps/web/src/features/commercial-proposals/components/CommercialProposalCalcParams.tsx
//
// @aiPurpose read-only "Calculation parameters" sub-block shown in the manager
//   drawer when a proposal is PUBLISHED (#1479). Lists the numeric calc inputs
//   and the applied markup %. Manager-internal only; customer sheet / public
//   DTO / PDF are not touched.
import { useTranslation } from 'react-i18next';
import type { CommercialProposalContent } from '@magimp/shared';

import type { AppLocale } from '../../../i18n/index.js';
import { formatQty } from '../format.js';
import { divisorToPercent } from '../formModel.js';

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 12,
        padding: '3px 0',
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>{label}</span>
      <span
        style={{
          fontSize: 13,
          color: 'var(--fg-1)',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    </div>
  );
}

export function CommercialProposalCalcParams({
  content,
  language,
}: {
  content: CommercialProposalContent;
  language: AppLocale;
}) {
  const { t } = useTranslation('commercialProposals');

  const showBuyout = content.mode === 'BOTH' || content.mode === 'BUYOUT';
  const showLogistics = content.mode === 'BOTH' || content.mode === 'LOGISTICS';

  const modeKey =
    content.mode === 'BOTH' ? 'both' : content.mode === 'BUYOUT' ? 'buyout' : 'logistics';

  let markupRow: React.ReactNode;
  if (content.paymentScheme === 'vat') {
    markupRow = (
      <Row
        label={t('calc.params.appliedMarkup')}
        value={`${formatQty(divisorToPercent(content.vatDivisor), language)} %`}
      />
    );
  } else if (content.paymentScheme === 'novat') {
    markupRow = (
      <Row
        label={t('calc.params.appliedMarkup')}
        value={`${formatQty(divisorToPercent(content.noVatDivisor), language)} %`}
      />
    );
  } else {
    markupRow = <Row label={t('calc.params.appliedMarkup')} value={t('calc.params.noMarkup')} />;
  }

  return (
    <div
      data-testid="commercial-proposals-calc-params"
      style={{
        marginTop: 18,
        padding: '14px 16px',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-2)',
        border: '1px solid var(--border-1)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          color: 'var(--fg-3)',
          marginBottom: 10,
        }}
      >
        {t('calc.params.title')}
      </div>

      <Row label={t('editor.fields.mode')} value={t(`editor.mode.${modeKey}`)} />

      {showBuyout && (
        <>
          <Row
            label={t('editor.fields.cnyRateRub')}
            value={formatQty(content.cnyRateRub, language)}
          />
          <Row label={t('editor.fields.goodsCny')} value={formatQty(content.goodsCny, language)} />
          <Row
            label={t('editor.fields.chinaDeliveryCny')}
            value={formatQty(content.chinaDeliveryCny, language)}
          />
          <Row
            label={t('editor.fields.servicePercent')}
            value={formatQty(content.servicePercent, language)}
          />
        </>
      )}

      {showLogistics && (
        <>
          <Row
            label={t('editor.fields.usdRateRub')}
            value={formatQty(content.usdRateRub, language)}
          />
          <Row label={t('editor.fields.weightKg')} value={formatQty(content.weightKg, language)} />
          <Row label={t('editor.fields.volumeM3')} value={formatQty(content.volumeM3, language)} />
          <Row
            label={`${t('editor.fields.tariffUsd')} ${t(`editor.tariffUnit.${content.tariffUnit}`)}`}
            value={formatQty(content.tariffUsdPerKg, language)}
          />
          <Row
            label={t('editor.fields.packagingUsd')}
            value={formatQty(content.packagingUsd, language)}
          />
          <Row label={t('editor.fields.prrUsd')} value={formatQty(content.prrUsd, language)} />
          <Row
            label={t('editor.fields.insurancePercent')}
            value={formatQty(content.insurancePercent, language)}
          />
          <Row
            label={t('editor.fields.insuranceCnyPerUsdRate')}
            value={formatQty(content.insuranceCnyPerUsdRate, language)}
          />
        </>
      )}

      <Row
        label={t('editor.fields.paymentScheme')}
        value={t(`editor.paymentScheme.${content.paymentScheme}`)}
      />

      {markupRow}
    </div>
  );
}
